/**
 * The write side of the Data Bar's "Bases" row: one debounced PATCH per burst
 * of checkbox toggles, at most one of them in flight at a time.
 *
 * The row used to batch its edits and send them when the popup closed. It now
 * saves as the user ticks (Shushanik, 2026-10-30), which is three problems at
 * once — a request per keystroke, two writes crossing on the wire, and a stale
 * response landing on top of a newer pick — so the whole of it lives here,
 * away from the component, where it can be driven by tests with fake timers.
 *
 * Three rules, and they are the three the row needs:
 *
 * 1. **Coalesce.** A toggle (re)starts the timer, so ticking four boxes in a
 *    second is one PATCH carrying all four. `flush()` is the same thing
 *    without the wait, for the popup closing or the row unmounting.
 * 2. **One in flight.** A toggle made while a PATCH is out does not race it:
 *    it is queued and sent when that PATCH settles, as ONE follow-up however
 *    many toggles arrived meanwhile. Two Bases writes can therefore never be
 *    open together, which is what keeps a slow early response from landing on
 *    the cache after a later one (`patchAhuTunnel` merges every response into
 *    the tunnel row).
 * 3. **Settle what was sent, not what is pending.** `onSettled` is handed the
 *    body that went out, so the caller drops exactly those toggles and keeps
 *    anything the user changed while it was in flight — the newer pick wins
 *    and is sent next.
 *
 * `build()` returns null when there is nothing to send — a popup opened and
 * closed, or a box ticked and un-ticked back to where the server already is.
 * `onIdle` fires on exactly that branch, and the caller MUST use it to drop
 * the toggles that came to nothing: a pending entry that is never dropped
 * keeps Default unticked on a tunnel that is fully automatic, and is sent as
 * an explicit override the next time anything else is written.
 *
 * Everything the row writes goes through here, Default included, which is
 * what makes "one in flight" true of the row rather than of side writes
 * alone: a reset clicked while a side PATCH is out is queued behind it, so
 * the server cannot apply them in the other order.
 */

export interface BaseSidesWriterOptions<TBody> {
  /** Debounce window in ms. */
  delay: number;
  /** The PATCH body for everything pending now, or null when that is nothing. */
  build: () => TBody | null;
  send: (body: TBody) => Promise<unknown>;
  /**
   * Called once per attempt, after it settles, with the body that was sent and
   * whether it succeeded. Drop the toggles it carried here.
   */
  onSettled: (body: TBody, ok: boolean) => void;
  /**
   * Called instead, when there was nothing to send. Drop the toggles that
   * came to nothing here — see the note on `build()` above.
   */
  onIdle?: () => void;
}

export interface BaseSidesWriter {
  /** A box was ticked: start (or restart) the debounce window. */
  schedule: () => void;
  /** Send now, if there is anything to send. */
  flush: () => void;
  /** Forget the pending window. Does not touch a PATCH already in flight. */
  cancel: () => void;
}

export const createBaseSidesWriter = <TBody>({
  delay,
  build,
  send,
  onSettled,
  onIdle,
}: BaseSidesWriterOptions<TBody>): BaseSidesWriter => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let saving = false;
  /** A toggle arrived while a PATCH was in flight. One flag, not a queue of
   * bodies: `build()` reads whatever is pending at the moment we send, so the
   * follow-up carries every toggle made in that window in one request. */
  let queued = false;

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const run = () => {
    clear();
    if (saving) {
      queued = true;
      return;
    }
    const body = build();
    if (body === null) {
      onIdle?.();
      return;
    }
    saving = true;
    let ok = true;
    Promise.resolve(send(body))
      .catch(() => {
        ok = false;
      })
      .then(() => {
        saving = false;
        // Settle first: the follow-up's `build()` has to see the toggles this
        // one carried already dropped, or it would send them a second time.
        onSettled(body, ok);
        if (queued) {
          queued = false;
          run();
        }
      });
  };

  return {
    schedule: () => {
      clear();
      timer = setTimeout(run, delay);
    },
    flush: run,
    cancel: () => {
      clear();
      queued = false;
    },
  };
};

/**
 * The PATCH body for a set of pending toggles: only the sides that end up
 * somewhere other than where the server already has them.
 *
 * A tick writes an override that outranks the automatic rule from then on, so
 * a box unticked and re-ticked inside one window must send nothing rather than
 * pin a side nobody meant to pin. `current` is the server's own answer
 * (`AHUTunnels.base_sides`), which is why this is compared against it and not
 * against the override columns: an untouched tunnel has no column at all.
 */
export const baseSidesPatchBody = <TSide extends string>(
  pending: Partial<Record<TSide, boolean>>,
  fieldFor: (side: TSide) => string | undefined,
  current: (side: TSide) => boolean
): Record<string, boolean> | null => {
  const body: Record<string, boolean> = {};
  (Object.keys(pending) as TSide[]).forEach((side) => {
    const field = fieldFor(side);
    if (!field) return;
    const value = Boolean(pending[side]);
    if (value === current(side)) return;
    body[field] = value;
  });
  return Object.keys(body).length === 0 ? null : body;
};

/**
 * Drop the pending toggles that have come to nothing: the ones whose value is
 * what the server already builds.
 *
 * A box ticked off and straight back on (or double-clicked) leaves an entry
 * that `baseSidesPatchBody` will never send. Left in the map it is not
 * harmless: `onDefault` is "nothing pending AND all four columns NULL", so
 * Default stays unticked on a tunnel nobody has actually overridden, the box
 * stops following the server, and the moment a neighbour's edit moves that
 * side's automatic answer the stale value goes out as an explicit override --
 * pinning a rail nobody chose, which is then built, weighed and priced.
 */
export const dropNoopBaseSides = <TSide extends string>(
  pending: Partial<Record<TSide, boolean>>,
  current: (side: TSide) => boolean
): Partial<Record<TSide, boolean>> => {
  const next: Partial<Record<TSide, boolean>> = { ...pending };
  (Object.keys(pending) as TSide[]).forEach((side) => {
    if (Boolean(pending[side]) === current(side)) delete next[side];
  });
  return next;
};

/**
 * Drop the toggles a settled PATCH carried, keeping any the user has changed
 * since it went out.
 *
 * `sent` is the body, keyed by COLUMN; `pending` is keyed by side. A side
 * whose pending value still matches what was sent has been written (or has
 * failed, and the row will show the server's answer either way), so it goes.
 * One the user moved again while the request was out stays, and the writer
 * sends it next.
 */
export const dropSettledBaseSides = <TSide extends string>(
  pending: Partial<Record<TSide, boolean>>,
  sent: Record<string, boolean>,
  fieldFor: (side: TSide) => string | undefined
): Partial<Record<TSide, boolean>> => {
  const next: Partial<Record<TSide, boolean>> = { ...pending };
  (Object.keys(pending) as TSide[]).forEach((side) => {
    const field = fieldFor(side);
    if (!field || !(field in sent)) return;
    if (Boolean(pending[side]) === sent[field]) delete next[side];
  });
  return next;
};
